var raf = require("enyo/animation");

/**
* A collection of utilities
* @module enyo-perf/utils
*/

/**
 * Wraps the provided callback function and returns a throttled version which
 * executes both on the leading ánd trailing edge of the defined timeout.
 *
 * Note that the method will only be executed on the trailing edge if it is
 * called twice in quick succession.
 *
 * @param   {Function}  callback  The function that will be throttled
 * @param   {Number}    limit     The minimum interval between throttled calls
 * @return  {Function}  The throttled function
 * @public
 */
exports.throttle = function(callback, limit) {
    var fn,
        timeout  = null,
        deferred = false,
        ctx,
        args;

    fn = function() {
        if (!timeout) {
            callback.apply(this, arguments);
            ctx      = null;
            args     = null;
            deferred = false;
            timeout  = setTimeout(function() {
                timeout  = null;
                deferred && fn.apply(ctx, args);
            }, limit);
        }
        else {
            /* eslint-disable consistent-this */
            ctx      = this;
            args     = arguments;
            deferred = true;
            /* eslint-enable consistent-this */
        }
    };

    fn.cancel = function() {
        clearTimeout(timeout);
        timeout  = null;
        deferred = false;
    };

    fn.flush = function() {
        clearTimeout(timeout);
        timeout  = null;
        deferred && fn.apply(ctx, args);
    };

    return fn;
};

/**
 * Wraps the provided callback function and returns a throttled version which
 * executes at most once per animationFrame. On first call, the function will be
 * executed immediately. The next call will be executed on the next frame.
 *
 * Note that the method will only be executed on the trailing edge if it is
 * called twice in quick succession.
 *
 * @param   {Function}  callback  The function that will be throttled
 * @return  {Function}  The throttled function
 * @public
 */
exports.frameThrottle = function(callback) {
    var fn,
        timeout  = null,
        deferred = false,
        ctx,
        args;

    fn = function() {
        if (!timeout) {
            callback.apply(this, arguments);
            ctx      = null;
            args     = null;
            deferred = false;
            timeout  = raf.requestAnimationFrame(function() {
                timeout  = null;
                deferred && fn.apply(ctx, args);
            });
        }
        else {
            /* eslint-disable consistent-this */
            ctx      = this;
            args     = arguments;
            deferred = true;
            /* eslint-enable consistent-this */
        }
    };

    fn.cancel = function() {
        raf.cancelAnimationFrame(timeout);
        timeout  = null;
        deferred = false;
    };

    fn.flush = function() {
        raf.cancelAnimationFrame(timeout);
        timeout  = null;
        deferred && fn.apply(ctx, args);
    };

    return fn;
};
